import { useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import HeroFinishes from "@/components/site/HeroFinishes";
import QuickRequestModal from "@/components/forms/QuickRequestModal";
import { useAuth } from "@/context/AuthContext";

export default function Hero() {
  const { user, authHeader } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section
      id="top"
      className="relative pt-24 pb-16 lg:pt-32 lg:pb-20 bg-bubbly overflow-hidden"
      data-testid="hero-section"
    >
      {/* Soft blobs */}
      <div className="blob blob-teal w-[340px] h-[340px] -top-28 -left-28" />
      <div className="blob blob-peach w-[300px] h-[300px] top-1/3 -right-20" />
      <div className="blob blob-butter w-[220px] h-[220px] bottom-0 left-1/3" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left */}
        <div className="lg:col-span-5 fade-up">
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.04] tracking-tight text-stone-900"
            data-testid="hero-headline"
          >
            Sketch your space, <br />
            <span className="italic font-medium">plan the future</span>
          </h1>

          <p className="mt-2 text-stone-700 text-sm font-semibold italic">Real Estate · Residential · Commercial</p>

          <p
            className="mt-5 max-w-xl text-sm text-stone-600 leading-relaxed"
            data-testid="hero-subheadline"
          >
            We measure on-site, draft with care, and deliver gorgeous 2D and 3D
            floor plans for the DC, Maryland & Virginia market. Friendly
            humans, fast turnaround.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors shadow-sm"
            >
              Request a Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 bg-white border border-stone-200 px-6 py-3 rounded-full text-sm font-semibold text-stone-900 hover:bg-stone-50 transition-colors"
            >
              Explore Services
            </a>
            <a
              href="#service-area"
              data-testid="hero-cta-service-area"
              className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-stone-900 px-2 py-3 border-b border-transparent hover:border-teal-700 transition-colors"
            >
              <MapPin className="h-4 w-4" />
              See Service Area
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-2xl">
            <Stat label="Turnaround" value="24–48h" tint="bg-teal-100 text-teal-800" testId="stat-turnaround" />
            <Stat label="Properties drawn" value="2,400+" tint="bg-amber-100 text-amber-800" testId="stat-properties" />
            <Stat label="Revisions included" value="5 rounds" tint="bg-emerald-100 text-emerald-800" testId="stat-revisions" />
            <Stat label="Location" value="DC Metropolitan Area" tint="bg-rose-100 text-rose-800" testId="stat-location" />
          </div>
        </div>

        {/* Right - Finishes slider with floating bits */}
        <div className="lg:col-span-7 relative" data-testid="hero-visual">
          <div className="relative">
            <HeroFinishes />
          </div>
        </div>
      </div>

      <QuickRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        prefill={{ name: user?.name, email: user?.email }}
        authHeader={authHeader}
      />
    </section>
  );
}

function Stat({ label, value, tint, testId }) {
  return (
    <div data-testid={testId} className="space-y-1">
      <span className={`inline-block ${tint} text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full`}>
        {label}
      </span>
      <div className="font-display text-xl font-semibold text-stone-900">{value}</div>
    </div>
  );
}
